import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref } from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { type BrazeSdk, loadBrazeSdk } from '@/plugins/braze'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

const BRAZE_WEB_SDK_FLAG = 'olympus.brazeWebSdk'
const REFRESH_WINDOW_MS = 60_000
const MAX_REFRESHES_PER_WINDOW = 3
const INIT_MAX_ATTEMPTS = 3
const INIT_RETRY_DELAY_MS = 5_000

const delay = async (ms: number): Promise<void> =>
  await new Promise((resolve) => setTimeout(resolve, ms))

export const useBrazeStore = defineStore('braze', () => {
  const brazeInitialized = ref(false)
  const authStore = useAuthStore()
  const featureFlags = useFeatureFlags()

  let braze: BrazeSdk | null = null
  const recentRefreshTimestamps: number[] = []

  // Isolates each init attempt from a later one. brazeInitialized doubles as
  // the in-progress lock and the shutdown sentinel, so a logout+login during
  // the retry delay would otherwise let a stale attempt resume and initialize
  // the already-live SDK, stranding the store (braze non-null while the
  // sentinel reads false, making shutdownBraze a silent no-op).
  let initGeneration = 0

  const fetchBrazeToken = async (): Promise<string> => {
    const response = await OlympusClient.auth.v2.hermes.getBrazeToken({})
    return response.token
  }

  const refreshBrazeToken = async (): Promise<void> => {
    if (!braze) return

    const cutoff = Date.now() - REFRESH_WINDOW_MS
    let oldest = recentRefreshTimestamps[0]
    while (oldest !== undefined && oldest < cutoff) {
      recentRefreshTimestamps.shift()
      oldest = recentRefreshTimestamps[0]
    }
    if (recentRefreshTimestamps.length >= MAX_REFRESHES_PER_WINDOW) return
    recentRefreshTimestamps.push(Date.now())

    try {
      const token = await fetchBrazeToken()
      if (!braze) return
      braze.setSdkAuthenticationSignature(token)
    } catch (e) {
      logError(e)
    }
  }

  const teardownBraze = (): void => {
    try {
      braze?.destroy()
    } catch (e) {
      logError(e)
    } finally {
      braze = null
      recentRefreshTimestamps.length = 0
      brazeInitialized.value = false
    }
  }

  // A transient first-login failure would otherwise strand Braze for the whole
  // session: the lifecycle watcher only re-fires on user change.
  const loadSdkWithRetry = async (
    gen: number
  ): Promise<{
    sdk: BrazeSdk
    token: string
  } | null> => {
    for (let attempt = 1; attempt <= INIT_MAX_ATTEMPTS; attempt++) {
      try {
        const [sdk, token] = await Promise.all([
          loadBrazeSdk(),
          fetchBrazeToken(),
        ])
        return { sdk, token }
      } catch (e) {
        logError(e)
        if (!brazeInitialized.value || initGeneration !== gen) return null
        if (attempt === INIT_MAX_ATTEMPTS) {
          teardownBraze()
          return null
        }
        await delay(INIT_RETRY_DELAY_MS)
        if (!brazeInitialized.value || initGeneration !== gen) return null
      }
    }
    return null
  }

  const initializeBraze = async (): Promise<void> => {
    if (!featureFlags.isEnabled(BRAZE_WEB_SDK_FLAG)) return
    if (brazeInitialized.value) return
    if (!authStore.isAuthenticated || !authStore.user) return

    const apiKey = import.meta.env.VITE_BRAZE_API_KEY
    const baseUrl = import.meta.env.VITE_BRAZE_SDK_ENDPOINT
    if (!apiKey || !baseUrl) return

    const userId = authStore.user.id
    const gen = ++initGeneration
    brazeInitialized.value = true

    const loaded = await loadSdkWithRetry(gen)
    if (!loaded || !brazeInitialized.value || initGeneration !== gen) return

    const { sdk, token } = loaded
    if (!sdk.initialize(apiKey, { baseUrl, enableSdkAuthentication: true })) {
      brazeInitialized.value = false
      return
    }

    braze = sdk
    sdk.subscribeToSdkAuthenticationFailures(() => {
      void refreshBrazeToken()
    })

    sdk.changeUser(userId, token)
    sdk.openSession()
  }

  const shutdownBraze = (): void => {
    if (!brazeInitialized.value) return
    teardownBraze()
  }

  return {
    brazeInitialized,
    initializeBraze,
    refreshBrazeToken,
    shutdownBraze,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBrazeStore, import.meta.hot))
}
